import Vue from 'vue';
import * as Sentry from '@sentry/vue';

const dsn = import.meta.env.VITE_SENTRY_DSN;
const environment = import.meta.env.VITE_MODE;
const release = import.meta.env.VITE_SENTRY_RELEASE || 'spa-local';

const OWN_DOMAINS = ['ulekare.cz', 'ulekare.local', 'soulody.com'];

function isOwnDomain(url) {
  if (!url) {
    return false;
  }
  try {
    const hostname = new URL(url).hostname;
    return OWN_DOMAINS.some(domain => hostname.endsWith(domain));
  } catch {
    // Relative URL — comes from our own axios baseURL
    return true;
  }
}

/**
 * Initialize Sentry error tracking.
 * Skipped when DSN or environment is not configured (e.g. local dev).
 */
export function initSentry(router) {
  if (!dsn || !environment) {
    return;
  }

  Sentry.init({
    Vue,
    dsn,
    environment,
    release,
    integrations: [Sentry.browserTracingIntegration({ router })],
    tracesSampleRate: environment === 'production' ? 0.1 : 0.5,
    ignoreErrors: [
      'ResizeObserver loop',
      'Request aborted',
      /Loading chunk .* failed/,
      /Navigation cancelled/,
      // Shen.AI WASM runtime can hit `Aborted()` / `unreachable` /
      // `null function` after `computeHealthRisks` or on SDK re-init.
      // Crash is internal to the SDK's rAF loop and doesn't surface to
      // the user (results already saved, navigation works). Filter the
      // noise so it doesn't drown real errors.
      /Aborted\(\)/,
      'RuntimeError: unreachable',
      'RuntimeError: null function',
      'worker sent an error'
    ],
    beforeSend(event, hint) {
      const error = hint?.originalException;

      // Drop axios Cancel errors
      if (error && error.__CANCEL__) {
        return null;
      }

      // Network Error — keep only for our own domains, drop third-party
      if (error?.message === 'Network Error') {
        const url = error.config?.url || error.config?.baseURL || '';
        if (!isOwnDomain(url)) {
          return null;
        }
      }

      // Anything originating from the Shen.AI SDK bundle is third-party
      // WASM — not our code, not actionable, drops in any error mode.
      const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
      if (frames.some(frame => /shenai_sdk\.mjs/.test(frame.filename ?? ''))) {
        return null;
      }

      return event;
    }
  });
}

/**
 * Set Sentry user context after login.
 */
export function setSentryUser(user) {
  if (!user) {
    return;
  }
  Sentry.setUser({ id: user.id, email: user.email });
}

/**
 * Clear Sentry user context after logout.
 */
export function clearSentryUser() {
  Sentry.setUser(null);
}

/**
 * Vuex store plugin — syncs auth user to Sentry context.
 */
export function SentryVuexPlugin(store) {
  store.subscribe(mutation => {
    if (mutation.type === 'auth/setUser') {
      if (mutation.payload) {
        setSentryUser(mutation.payload);
      } else {
        clearSentryUser();
      }
    }
  });
}
